import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
} from "@angular/core";
import {TranslateModule} from "@ngx-translate/core";
import {StreamingTypes} from "src/app/enums/StreamingType";
import {StreamingOption} from "src/app/models/Movie";
import {WorksModel} from "src/app/models/WorksModel";
import {hasStreaming} from "src/app/utils/works-type-guards.util";
import {DisplayStreamingOptionComponent} from "../display-streaming-option/display-streaming-option.component";
import {SlicePipe} from "@angular/common";
import {GuardTypePipe} from "../../../core/pipes/guardType.pipe";
import {UserLocaleService} from "src/app/services/user-locale.service";
import {
  allowsCrossRegionTranslationFallback,
  resolveTranslation,
} from "src/app/utils/translation.util";
import {User} from "src/app/modules/auth/models/User";
import {WorksService} from "src/app/modules/shared/services/works.service";

@Component({
  selector: "app-display-streaming-options",
  templateUrl: "./display-streaming-options.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DisplayStreamingOptionComponent,
    SlicePipe,
    GuardTypePipe,
    TranslateModule,
  ],
})
export class DisplayStreamingOptionsComponent implements OnChanges {
  @Input() item!: WorksModel;
  @Input() user?: User | null;
  public streamingTypes = StreamingTypes;
  public hasStreaming = hasStreaming;
  public streamingOptions: StreamingOption[] = [];

  constructor(
    private readonly userLocale: UserLocaleService,
    private readonly worksService: WorksService
  ) {}

  ngOnChanges(): void {
    this.streamingOptions = [];
    if (!this.item || !hasStreaming(this.item)) return;
    const episode = this.worksService.currentEpisodeStreaming(this.item);
    this.streamingOptions = this.getWorkStreamingOptions([
      ...(episode?.streamingOptions ?? []),
      ...(this.item.streamingAvailability?.["fr"]?.streamingOptions ?? []),
    ]);
  }

  public getItemTranslation(item: WorksModel, field: string): string {
    const locale = this.userLocale.getLocale();
    if (locale.split("-")[0] !== "en") {
      const translated = resolveTranslation(item.translations, field, locale, {
        crossRegionFallback: allowsCrossRegionTranslationFallback(field),
      });
      if (translated) {
        return translated;
      }
    }

    return item[field as keyof WorksModel] as string;
  }

  public getWorkStreamingOptions(
    streamingOptions: StreamingOption[]
  ): StreamingOption[] {
    const uniqueServices = new Map<string, StreamingOption>();

    for (const option of streamingOptions) {
      const key = option.service.id;
      const current = uniqueServices.get(key);
      if (!current || (!current.link && option.link)) {
        uniqueServices.set(key, option);
      }
    }

    const sortedOptions = Array.from(uniqueServices.values()).sort((a, b) => {
      // StreamingTypes priorities : subscription > rent > buy
      const typePriority: {[key in StreamingTypes]: number} = {
        subscription: 1,
        free: 2,
        rent: 3,
        buy: 4,
        addon: 5,
      };

      const typeComparison =
        (typePriority[a.type as StreamingTypes] || 6) -
        (typePriority[b.type as StreamingTypes] || 6);

      if (typeComparison !== 0) {
        return typeComparison;
      }

      // Sort by service name
      return a.service.name.localeCompare(b.service.name);
    });

    return sortedOptions;
  }

  trackByStreamingOption(_index: number, item: StreamingOption): string {
    return item.service.id;
  }

  isAvailableForUser(streamingOption: StreamingOption): boolean {
    return this.worksService.isStreamingOptionAvailableForUser(
      streamingOption,
      this.user
    );
  }
}
